import { apiClient } from './client'

export interface LibraryDefinition {
  id: string
  orgId: number | null
  isProduct: boolean
  slug: string
  name: string
  description: string | null
  category: string | null
  tags: string[]
  visibility: string
  status: string
  usageCount: number
  lastUsedAt: string | null
  createdBy: number
  createdAt: string
  updatedAt: string
  archivedAt: string | null
}

export interface LibraryVersionMeta {
  version: number
  status: string
  contentHash: string
  changeSummary: string | null
  createdBy: number
  createdAt: string
  publishedAt: string | null
}

export interface LibraryActiveVersion extends LibraryVersionMeta {
  instructions: string
  variableSchema: Record<string, unknown>
  contextPolicy: Record<string, unknown>
  requiredTools: string[]
  compatibleAgents: string[]
}

export interface LibraryBinding {
  id: string
  scopeType: string
  scopeId: number
  inheritanceMode: string
  versionMode: string
  enabled: boolean
  revision: number
}

export interface LibraryDefinitionDetail extends LibraryDefinition {
  versions: LibraryVersionMeta[]
  activeVersion: LibraryActiveVersion | null
  bindings: LibraryBinding[]
}

export interface ResolvedPromptEntry {
  id: string
  slug: string
  name: string
  isProduct: boolean
  status: string
  body: string | null
  compileNote: string | null
  fingerprint: string | null
  version: number | null
  projectBindingId: string | null
  projectBindingMode: string | null
  areaBindingId: string | null
}

export interface ResolvedPromptsView {
  inherited: ResolvedPromptEntry[]
  local: ResolvedPromptEntry[]
  disabled: ResolvedPromptEntry[]
}

export interface CompiledLayer {
  scopeType: string
  scopeId: number | null
  scopeName: string | null
  mode: string
  versionId: string | null
  bindingRevision: number | null
  included: boolean
  reason: string | null
  tokenContribution: number
}

export interface CompileResult {
  status: 'COMPILED' | 'DISABLED'
  definitionId: string
  definitionSlug: string
  versionId: string | null
  version: number | null
  effectiveInstructions: string
  layers: CompiledLayer[]
  warnings: string[]
  estimatedTokens: { instructions: number; context: number; total: number }
  compilationFingerprint: string
  compilerVersion: string
  mergedPolicy: Record<string, unknown>
}

export interface BindingInput {
  scopeType: 'ORGANIZATION' | 'PROJECT' | 'KNOWLEDGE_AREA'
  scopeId: number
  projectId?: number
  inheritanceMode: 'append' | 'prepend' | 'replace' | 'disable'
  instructionsOverride?: string | null
  revision?: number
}

function params(query: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, value)
  }
  const text = search.toString()
  return text.length > 0 ? `?${text}` : ''
}

export const promptLibraryApi = {
  list: (query?: { status?: string; category?: string; q?: string; scope?: string }) =>
    apiClient.get<LibraryDefinition[]>(`/prompt-library${params(query ?? {})}`),
  get: (idOrSlug: string) =>
    apiClient.get<LibraryDefinitionDetail>(`/prompt-library/${encodeURIComponent(idOrSlug)}`),
  create: (body: {
    name: string
    slug?: string
    description?: string
    category?: string
    tags?: string[]
    visibility?: 'TENANT' | 'PRIVATE'
    isProduct?: boolean
  }) => apiClient.post<LibraryDefinition>('/prompt-library', body),
  createVersion: (id: string, body: { instructions: string; changeSummary?: string }) =>
    apiClient.post<LibraryVersionMeta>(`/prompt-library/${id}/versions`, body),
  publishVersion: (id: string, version: number) =>
    apiClient.post<LibraryVersionMeta>(`/prompt-library/${id}/versions/${version}/publish`),
  deprecateVersion: (id: string, version: number) =>
    apiClient.post<LibraryVersionMeta>(`/prompt-library/${id}/versions/${version}/deprecate`),
  updateDefinition: (
    id: string,
    body: {
      name?: string
      description?: string | null
      category?: string | null
      tags?: string[]
      status?: 'DRAFT' | 'ACTIVE' | 'DEPRECATED'
    }
  ) => apiClient.patch<LibraryDefinition>(`/prompt-library/${encodeURIComponent(id)}`, body),
  archive: (id: string) =>
    apiClient.patch<LibraryDefinition>(`/prompt-library/${encodeURIComponent(id)}`, {
      status: 'ARCHIVED',
    }),
  restore: (id: string) =>
    apiClient.patch<LibraryDefinition>(`/prompt-library/${encodeURIComponent(id)}`, {
      status: 'RESTORED',
    }),
  upsertBinding: (id: string, body: BindingInput) =>
    apiClient.post<LibraryBinding>(`/prompt-library/${id}/bindings`, body),
  patchBinding: (
    id: string,
    bindingId: string,
    body: {
      enabled?: boolean
      revision?: number
      inheritanceMode?: 'append' | 'prepend' | 'replace' | 'disable'
      instructionsOverride?: string | null
    }
  ) => apiClient.patch<LibraryBinding>(`/prompt-library/${id}/bindings/${bindingId}`, body),
  fork: (id: string, body: { name: string }) =>
    apiClient.post<{ definition: LibraryDefinition; version: number; imported: boolean }>(
      `/prompt-library/${id}/fork`,
      body
    ),
  resolve: (idOrSlug: string, body: { projectId?: number; knowledgeAreaId?: number }) =>
    apiClient.post<CompileResult>(`/prompt-library/${encodeURIComponent(idOrSlug)}/resolve`, {
      ...body,
      mode: 'PREVIEW',
    }),
  compilePreview: (
    idOrSlug: string,
    body: { projectId?: number; knowledgeAreaId?: number; variables?: Record<string, unknown> }
  ) =>
    apiClient.post<CompileResult>(`/prompt-library/${encodeURIComponent(idOrSlug)}/compile`, {
      ...body,
      mode: 'PREVIEW',
    }),
  resolvedView: (projectId: number, knowledgeAreaId?: number) =>
    apiClient.get<ResolvedPromptsView>(
      knowledgeAreaId === undefined
        ? `/projects/${projectId}/prompts/resolved`
        : `/projects/${projectId}/knowledge-areas/${knowledgeAreaId}/prompts/resolved`
    ),
}
